import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { Bell, Check } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

function NotificationsPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/auth/me/notifications`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch preferences');
      return response.json();
    },
    enabled: !!accessToken,
  });

  const updateMutation = useMutation({
    mutationFn: async (prefs: { emailAlerts?: boolean; emailDigest?: string }) => {
      const response = await fetch(`${API_URL}/v1/auth/me/notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(prefs),
      });
      if (!response.ok) throw new Error('Failed to update preferences');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setMessage({ type: 'success', text: 'Preferences updated' });
      setTimeout(() => setMessage(null), 3000);
    },
  });

  const preferences = data?.preferences;

  const subNavItems = [
    { to: '/profile', label: 'Basic Info' },
    { to: '/profile/notifications', label: 'Notifications' },
    { to: '/profile/delete', label: 'Delete Account' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
        <p className="text-muted-foreground">Manage how you receive notifications</p>
      </div>

      <SubNav items={subNavItems} />

      <div className="max-w-2xl">
        {message && (
          <div
            className={`mb-6 flex items-center gap-2 rounded-lg p-4 ${
              message.type === 'success'
                ? 'bg-green-500/15 text-green-400'
                : 'bg-destructive/15 text-destructive'
            }`}
          >
            {message.type === 'success' && <Check className="h-4 w-4" />}
            {message.text}
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Email Notifications</CardTitle>
                <CardDescription>Configure your email notification preferences</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Email Alerts</Label>
                <p className="text-sm text-muted-foreground">
                  Receive email notifications when alerts are triggered
                </p>
              </div>
              <Switch
                checked={preferences?.emailAlerts ?? true}
                onCheckedChange={(checked) => updateMutation.mutate({ emailAlerts: checked })}
                aria-label="Email Alerts"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Digest</Label>
              <p className="text-sm text-muted-foreground">
                How often to receive summary emails
              </p>
              <Select
                value={preferences?.emailDigest ?? 'daily'}
                onValueChange={(value) => updateMutation.mutate({ emailDigest: value })}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/profile/notifications')({
  component: NotificationsPage,
});
